import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { WhatsAppConnection, WhatsAppConnectionInput } from '../../types/config';
import { ApiResponse } from '../../types/api';
import { utils } from '../../utils';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import WhatsAppAdvancedSettings from '../../components/WhatsApp/WhatsAppAdvancedSettings';
import ChatbotSettings from '../../components/WhatsApp/ChatbotSettings';
import ModalPortal from '../../components/Common/ModalPortal';
import EmailConnections from './EmailConnections';
import '../../styles/whatsapp-advanced.css';

const WhatsApp: React.FC = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState<WhatsAppConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<WhatsAppConnection | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState<WhatsAppConnectionInput>({
    nome: '',
    empresa_id: user?.empresa_id || 0, // Será preenchido automaticamente
    numero_telefone: ''
  });

  // States for editing
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingConnection, setEditingConnection] = useState<WhatsAppConnection | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [connectLoading, setConnectLoading] = useState<number | null>(null);
  const [qrRetryCount, setQrRetryCount] = useState(0);
  const MAX_QR_RETRIES = 10; // Limite de 10 tentativas
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [advancedSettings, setAdvancedSettings] = useState<Partial<WhatsAppConnection>>({});
  const [webhookSaving, setWebhookSaving] = useState(false);

  // Ref para controlar o polling do QR code
  const qrPollingInterval = useRef<NodeJS.Timeout | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'whatsapp' | 'email'>('whatsapp');

  useEffect(() => {
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verificar status da conexão a cada 3 segundos quando modal está aberto
  useEffect(() => {
    if (!selectedConnection) return;

    const statusCheckInterval = setInterval(async () => {
      try {
        // Buscar status atualizado do servidor
        const response = await apiService.getWhatsAppConnections(user?.empresa_id);
        if (response.success && response.data) {
          const updatedConnection = response.data.find((conn: WhatsAppConnection) => conn.id === selectedConnection.id);

          if (updatedConnection) {
            console.log(`📊 Status atual: ${updatedConnection.status}`);

            // Se conectou, fechar modal e recarregar lista
            if (updatedConnection.status === 'conectado') {
              console.log('✅ WhatsApp conectado! Fechando modal...');
              utils.showNotification('WhatsApp conectado com sucesso!', { type: 'success' });

              // Fechar modal
              setSelectedConnection(null);
              setQrCode(null);
              setQrRetryCount(0);

              // Recarregar lista de conexões
              loadConnections();
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status:', error);
      }
    }, 3000); // Verificar a cada 3 segundos

    return () => {
      clearInterval(statusCheckInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnection, user?.empresa_id]);

  // Renovação automática do QR Code a cada 60 segundos (máximo 10 tentativas)
  useEffect(() => {
    if (selectedConnection && selectedConnection.status === 'conectando' && qrRetryCount < MAX_QR_RETRIES) {
      qrPollingInterval.current = setInterval(async () => {
        // ✅ SEMPRE RENOVA QR CODE - nunca reutiliza o mesmo!
        console.log(`🔄 Renovando QR Code automaticamente... Tentativa ${qrRetryCount + 1}/${MAX_QR_RETRIES}`);

        try {
          // Forçar QR Code sempre novo diretamente da API usando a nova rota
          // A rota espera o NOME da instância, não o ID
          const qrResponse = await apiService.get<any>('WHATSAPP_INSTANCE_CONNECT', { instanceName: selectedConnection.nome });

          if (qrResponse.success && qrResponse.qr_code) {
            // ✅ SUBSTITUIR QR Code anterior por um sempre novo
            const sanitizedQrCode = sanitizeBase64Image(qrResponse.qr_code);

            // Forçar atualização visual: primeiro limpar, depois setar novo
            setQrCode(null);
            setTimeout(() => {
              setQrCode(sanitizedQrCode);
              setQrRetryCount(prev => prev + 1);
              console.log(`✅ QR Code renovado - Tentativa ${qrRetryCount + 1} concluída`);
            }, 100);
          }
        } catch (error) {
          console.error('❌ Erro ao renovar QR Code:', error);
        }
      }, 60000); // Exato 60 segundos

      return () => {
        if (qrPollingInterval.current) {
          clearInterval(qrPollingInterval.current);
        }
      };
    }

    // Parar quando atingir 10 tentativas OU desconnectar
    return () => {
      if (qrPollingInterval.current) {
        clearInterval(qrPollingInterval.current);
        qrPollingInterval.current = null;
      }
    };
  }, [selectedConnection, qrRetryCount]);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const response = await apiService.getWhatsAppConnections(user?.empresa_id);
      setConnections(response.success ? response.data || [] : []);
    } catch (error) {
      console.error('Erro ao carregar conexões WhatsApp:', error);
      utils.showNotification('Erro ao carregar conexões WhatsApp', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim() || !formData.numero_telefone.trim()) {
      utils.showNotification('Nome e número de telefone são obrigatórios', { type: 'error' });
      return;
    }

    try {
      await apiService.post('WHATSAPP_CONNECTIONS', {
        ...formData,
        ...advancedSettings,
        empresa_id: user?.empresa_id || 1
      });

      utils.showNotification('Conexão WhatsApp criada com sucesso!', { type: 'success' });
      setShowCreateModal(false);
      setFormData({ nome: '', empresa_id: user?.empresa_id || 0, numero_telefone: '' });
      setAdvancedSettings({});

      loadConnections(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao criar conexão WhatsApp:', error);
      utils.showNotification('Erro ao criar conexão WhatsApp', { type: 'error' });
    }
  };

  const handleConnect = async (connectionId: number) => {
    setConnectLoading(connectionId); // Adicionar loading state

    try {
      // Chamar API de conectar e obter a resposta com o status atualizado
      const connectResponse = await apiService.post<ApiResponse<WhatsAppConnection>>('WHATSAPP_CONNECT', {}, { id: connectionId });

      if (connectResponse.success && connectResponse.data) {
        const updatedConnection = connectResponse.data;

        utils.showNotification('Iniciando conexão WhatsApp...', { type: 'info' });

        // Se a conexão retornou com status 'conectando', mostrar modal QR imediatamente
        if (updatedConnection.status === 'conectando') {
          console.log('✅ Conexão iniciada. Mostrando modal QR...');
          setTimeout(() => {
            showQrCode(updatedConnection);
          }, 500); // Pequeno delay para garantir que o QR code esteja disponível
        } else if (updatedConnection.status === 'conectado') {
          utils.showNotification('WhatsApp já está conectado!', { type: 'success' });
        }

        // Recarregar conexões para atualizar a lista com status correto
        await loadConnections();
      } else {
        throw new Error('Resposta da API inválida');
      }
    } catch (error) {
      console.error('Erro ao conectar WhatsApp:', error);
      utils.showNotification('Erro ao conectar WhatsApp', { type: 'error' });
    } finally {
      setConnectLoading(null); // Remover loading state
    }
  };

  const handleDisconnect = async (connectionId: number) => {
    try {
      await apiService.post('WHATSAPP_DISCONNECT', {}, { id: connectionId });

      utils.showNotification('WhatsApp desconectado com sucesso!', { type: 'success' });
      loadConnections(); // Recarregar para atualizar status
    } catch (error) {
      console.error('Erro ao desconectar WhatsApp:', error);
      utils.showNotification('Erro ao desconectar WhatsApp', { type: 'error' });
    }
  };

  const showQrCode = async (connection: WhatsAppConnection) => {
    setSelectedConnection(connection);
    setQrLoading(true);
    // Resetar contadores quando uma nova tentativa é iniciada
    setQrRetryCount(0);

    try {
      // Usar a nova rota de instância em vez da rota antiga
      // A rota espera o NOME da instância, não o ID
      console.log('🔄 Iniciando obtenção do QR Code para:', connection.nome);
      const response = await apiService.get<any>('WHATSAPP_INSTANCE_CONNECT', { instanceName: connection.nome });
      
      console.log('📡 Resposta da API:', {
        success: response.success,
        hasQrCode: !!response.qr_code,
        qrCodeLength: response.qr_code?.length,
        message: response.message
      });
      
      if (response.success && response.qr_code) {
        // Sanitizar e validar a string base64
        console.log('🧹 Sanitizando QR Code...');
        const sanitizedQrCode = sanitizeBase64Image(response.qr_code);
        console.log('✅ QR Code sanitizado:', {
          isNull: sanitizedQrCode === null,
          length: sanitizedQrCode?.length,
          preview: sanitizedQrCode?.substring(0, 50) + '...'
        });
        
        console.log('📝 Setando estado qrCode...');
        setQrCode(sanitizedQrCode);
        
        // Verificar se o estado foi atualizado
        setTimeout(() => {
          console.log('🔍 Estado qrCode após set:', qrCode);
        }, 100);
      } else {
        console.log('❌ QR Code não disponível na resposta');
        setQrCode(null);
      }
    } catch (error) {
      console.error('❌ Erro ao obter QR Code:', error);
      utils.showNotification('QR Code não disponível', { type: 'error' });
      setQrCode(null);
    } finally {
      setQrLoading(false);
    }
  };

  // Função para sanitizar e validar strings base64 de imagem
  const sanitizeBase64Image = (base64String: string): string | null => {
    if (!base64String) return null;
    
    try {
      // Remover espaços em branco e quebras de linha
      let sanitized = base64String.trim().replace(/\s/g, '');
      
      // Verificar se já é um data URL completo
      if (sanitized.startsWith('data:image/')) {
        // Se já é um data URL, retornar como está
        return sanitized;
      } else {
        // Se for apenas base64, criar data URL completo
        // Não validar o base64, apenas criar o data URL
        return `data:image/png;base64,${sanitized}`;
      }
    } catch (error) {
      console.error('Erro ao sanitizar base64:', error);
      // Em caso de erro, retornar null
      return null;
    }
  };



  const handleEdit = (connection: WhatsAppConnection) => {
    setEditingConnection(connection);
    setFormData({
      nome: connection.nome,
      empresa_id: connection.empresa_id,
      numero_telefone: connection.numero_telefone
    });
    setAdvancedSettings({
      webhook_enabled: connection.webhook_enabled,
      webhook_url: connection.webhook_url,
      webhook_headers: connection.webhook_headers,
      webhook_by_events: connection.webhook_by_events,
      webhook_base64: connection.webhook_base64,
      webhook_events: connection.webhook_events,
      profile_name: connection.profile_name,
      profile_status: connection.profile_status,
      profile_picture_url: connection.profile_picture_url,
      privacy_settings: connection.privacy_settings,
      settings: connection.settings,
      presence: connection.presence,
      business_profile: connection.business_profile,
      connection_data: connection.connection_data,
      qr_code_data: connection.qr_code_data
    });
    setIsEditMode(true);
    setShowAdvancedSettings(true);
    setShowCreateModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingConnection) return;

    if (!formData.nome.trim() || !formData.numero_telefone.trim()) {
      utils.showNotification('Nome e número de telefone são obrigatórios', { type: 'error' });
      return;
    }

    try {
      await apiService.put('WHATSAPP_CONNECTION_BY_ID', {
        ...formData,
        ...advancedSettings
      }, { id: editingConnection.id });

      utils.showNotification('Conexão WhatsApp atualizada com sucesso!', { type: 'success' });
      setShowCreateModal(false);
      resetForm();

      loadConnections(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao atualizar conexão WhatsApp:', error);
      utils.showNotification('Erro ao atualizar conexão WhatsApp', { type: 'error' });
    }
  };

  const handleDelete = async (connectionId: number) => {
    const confirmDelete = window.confirm('Tem certeza que deseja excluir esta conexão WhatsApp? Esta ação não pode ser desfeita.');

    if (!confirmDelete) return;

    setDeleteLoading(connectionId);

    try {
      await apiService.delete('WHATSAPP_CONNECTION_BY_ID', { id: connectionId });

      utils.showNotification('Conexão WhatsApp excluída com sucesso!', { type: 'success' });
      loadConnections(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao excluir conexão WhatsApp:', error);
      utils.showNotification('Erro ao excluir conexão WhatsApp', { type: 'error' });
    } finally {
      setDeleteLoading(null);
    }
  };

  // Função para salvar configurações de webhook
  const handleSaveWebhook = async (webhookSettings: Partial<WhatsAppConnection>) => {
    if (!editingConnection) {
      utils.showNotification('Nenhuma conexão selecionada para salvar configurações', { type: 'error' });
      return;
    }

    setWebhookSaving(true);

    try {
      // Salvar apenas as configurações de webhook
      await apiService.put('WHATSAPP_CONNECTION_BY_ID', {
        webhook_enabled: webhookSettings.webhook_enabled,
        webhook_url: webhookSettings.webhook_url,
        webhook_headers: webhookSettings.webhook_headers,
        webhook_by_events: webhookSettings.webhook_by_events,
        webhook_base64: webhookSettings.webhook_base64,
        webhook_events: webhookSettings.webhook_events
      }, { id: editingConnection.id });

      utils.showNotification('Configurações de Webhook salvas com sucesso!', { type: 'success' });
      
      // Recarregar conexões para atualizar dados
      await loadConnections();
      
      // Atualizar estado local
      setAdvancedSettings(prev => ({ ...prev, ...webhookSettings }));
      
    } catch (error) {
      console.error('Erro ao salvar configurações de webhook:', error);
      utils.showNotification('Erro ao salvar configurações de webhook', { type: 'error' });
    } finally {
      setWebhookSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ nome: '', empresa_id: user?.empresa_id || 0, numero_telefone: '' });
    setAdvancedSettings({});
    setShowAdvancedSettings(false);
    setIsEditMode(false);
    setEditingConnection(null);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const renderStatusBadge = (status: string) => {
    const statusConfig = {
      conectado: { class: 'badge-success', text: 'Conectado' },
      conectando: { class: 'badge-warning', text: 'Conectando' },
      desconectado: { class: 'badge-danger', text: 'Desconectado' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.desconectado;

    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Conexões de Comunicação</h1>
          <p>Gerencie conexões WhatsApp e Email para envio automático de documentos</p>
        </div>

        {activeTab === 'whatsapp' && (
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fas fa-plus"></i>
            Nova Conexão
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '0', marginBottom: '1.5rem',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <button
          onClick={() => setActiveTab('whatsapp')}
          style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer',
            fontSize: '0.9rem', fontWeight: 600,
            background: 'transparent',
            color: activeTab === 'whatsapp' ? '#04364F' : '#7f8c8d',
            borderBottom: activeTab === 'whatsapp' ? '3px solid #04364F' : '3px solid transparent',
            marginBottom: '-2px'
          }}>
          WhatsApp
        </button>
        <button
          onClick={() => setActiveTab('email')}
          style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer',
            fontSize: '0.9rem', fontWeight: 600,
            background: 'transparent',
            color: activeTab === 'email' ? '#04364F' : '#7f8c8d',
            borderBottom: activeTab === 'email' ? '3px solid #04364F' : '3px solid transparent',
            marginBottom: '-2px'
          }}>
          Email (SMTP)
        </button>
      </div>

      {activeTab === 'email' ? (
        <div className="page-content">
          <EmailConnections />
        </div>
      ) : (
      <div className="page-content">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="whatsapp-grid">
            {connections.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-comment-dots empty-icon"></i>
                <h3>Nenhuma conexão WhatsApp</h3>
                <p>Crie sua primeira conexão para começar a receber notificações automáticas</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Criar Primeira Conexão
                </button>
              </div>
            ) : (
              connections.map((connection) => (
                <div key={connection.id} className="whatsapp-card">
                  <div className="whatsapp-card-header">
                    <h3>{connection.nome}</h3>
                    {renderStatusBadge(connection.status)}
                  </div>

                  <div className="whatsapp-card-body">
                    <div className="whatsapp-info">
                      <p><strong>Telefone:</strong> {connection.numero_telefone}</p>
                      {connection.ultima_conexao && (
                        <p><strong>Última conexão:</strong> {new Date(connection.ultima_conexao).toLocaleString('pt-BR')}</p>
                      )}
                      <p><strong>Criado em:</strong> {new Date(connection.criado_em).toLocaleDateString('pt-BR')}</p>
                    </div>

                    <div className="whatsapp-actions">
                      {connection.status === 'desconectado' && (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleConnect(connection.id)}
                          disabled={connectLoading === connection.id}
                        >
                          {connectLoading === connection.id ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i>
                              Conectando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-plug"></i>
                              Conectar
                            </>
                          )}
                        </button>
                      )}

                      {connection.status === 'conectado' && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDisconnect(connection.id)}
                        >
                          <i className="fas fa-unplug"></i>
                          Desconectar
                        </button>
                      )}

                      {connection.status === 'conectando' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => showQrCode(connection)}
                        >
                          <i className="fas fa-qrcode"></i>
                          QR Code
                        </button>
                      )}

                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEdit(connection)}
                      >
                        <i className="fas fa-edit"></i>
                        Editar
                      </button>

                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(connection.id)}
                        disabled={deleteLoading === connection.id}
                      >
                        {deleteLoading === connection.id ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Excluindo...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-trash"></i>
                            Excluir
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Modal de Criação/Edição */}
        <ModalPortal isOpen={showCreateModal}>
          <div className="modal-overlay">
            <div
              className={`modal-content ${showAdvancedSettings ? 'modal-large' : ''}`}
              style={showAdvancedSettings ? { maxWidth: 960, maxHeight: '95vh', display: 'flex', flexDirection: 'column' } : undefined}
            >
              <div className="modal-header" style={{ flexShrink: 0 }}>
                <h3>
                  {isEditMode ? (
                    <><i className="fas fa-edit" style={{ marginRight: 8, color: 'var(--primary-color, #007bff)' }}></i>Editar Conexão</>
                  ) : (
                    <><i className="fas fa-plus-circle" style={{ marginRight: 8, color: 'var(--primary-color, #007bff)' }}></i>Nova Conexão WhatsApp</>
                  )}
                </h3>
                <button type="button" className="btn-close" onClick={handleModalClose}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {/* Dados Básicos - dentro de um form isolado */}
                <form id="whatsapp-connection-form" onSubmit={isEditMode ? handleUpdate : handleSubmit}>
                  <div style={{ padding: '20px 24px', borderBottom: showAdvancedSettings ? '1px solid #e9ecef' : 'none' }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label htmlFor="nome">Nome da Conexão *</label>
                        <input
                          type="text"
                          id="nome"
                          value={formData.nome}
                          onChange={(e) => setFormData({...formData, nome: e.target.value})}
                          placeholder="Ex: Empresa XYZ - Motoristas"
                          required
                        />
                      </div>
                      <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                        <label htmlFor="numero_telefone">Número do WhatsApp *</label>
                        <input
                          type="tel"
                          id="numero_telefone"
                          value={formData.numero_telefone}
                          onChange={(e) => setFormData({...formData, numero_telefone: e.target.value})}
                          placeholder="Ex: 5511999999999"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </form>

                {/* Configurações Avançadas - fora do form para não disparar submit */}
                {showAdvancedSettings && (
                  <div>
                    <div style={{ padding: '16px 24px 0' }}>
                      <h4 style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="fas fa-cog" style={{ color: 'var(--primary-color, #007bff)' }}></i>
                        Configurações Avançadas
                      </h4>
                    </div>

                    <WhatsAppAdvancedSettings
                      connection={{
                        ...formData,
                        ...advancedSettings,
                        id: editingConnection?.id || 0,
                        status: editingConnection?.status || 'desconectado',
                        criado_em: editingConnection?.criado_em || new Date().toISOString(),
                        atualizado_em: editingConnection?.atualizado_em || new Date().toISOString()
                      }}
                      onSettingsChange={(settings) => {
                        setAdvancedSettings(prev => ({ ...prev, ...settings }));
                      }}
                      onSaveWebhook={handleSaveWebhook}
                      webhookSaving={webhookSaving}
                      isConnected={editingConnection?.status === 'conectado' || false}
                    />

                    {/* Chatbot */}
                    {isEditMode && editingConnection?.id && (
                      <div style={{ padding: '0 24px 20px', marginTop: 16 }}>
                        <h4 style={{
                          fontSize: 15, fontWeight: 600, color: '#333',
                          paddingBottom: 10, marginBottom: 12,
                          borderBottom: '2px solid var(--primary-color, #007bff)',
                          display: 'flex', alignItems: 'center', gap: 8
                        }}>
                          <i className="fas fa-robot" style={{ color: 'var(--primary-color, #007bff)' }}></i>
                          Chatbot
                        </h4>
                        <ChatbotSettings
                          connection={{
                            ...formData,
                            ...advancedSettings,
                            id: editingConnection.id,
                            status: editingConnection.status || 'desconectado',
                            criado_em: editingConnection.criado_em || new Date().toISOString(),
                            atualizado_em: editingConnection.atualizado_em || new Date().toISOString()
                          }}
                          onSettingsChange={(settings) => {
                            setAdvancedSettings(prev => ({ ...prev, ...settings }));
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer" style={{ flexShrink: 0 }}>
                <div className="modal-footer-left">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  >
                    <i className={`fas ${showAdvancedSettings ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                    {showAdvancedSettings ? 'Ocultar Configurações' : 'Configurações Avançadas'}
                  </button>
                </div>
                <div className="modal-footer-right">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleModalClose}
                  >
                    Cancelar
                  </button>
                  <button type="submit" form="whatsapp-connection-form" className="btn btn-primary">
                    <i className={`fas ${isEditMode ? 'fa-save' : 'fa-plus'}`} style={{ marginRight: 6 }}></i>
                    {isEditMode ? 'Salvar Conexão' : 'Criar Conexão'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>

        {/* Modal do QR Code */}
        <ModalPortal isOpen={!!selectedConnection}>
          <div className="modal-overlay">
            <div className="modal-content qr-modal">
              <div className="modal-header">
                <h3>QR Code - {selectedConnection?.nome}</h3>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSelectedConnection(null);
                    setQrCode(null);
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body">
                {qrLoading ? (
                  <div className="qr-loading">
                    <LoadingSpinner />
                    <p>Gerando QR Code...</p>
                  </div>
                ) : qrCode ? (
                  <div className="qr-modal-content">
                    <div className="qr-code-wrapper">
                      <img
                        key={qrRetryCount}  // Forçar recriação do elemento a cada renovação
                        src={qrCode}
                        alt="QR Code WhatsApp"
                        className="qr-code-image"
                        onError={(e) => {
                          console.error('Erro ao carregar QR Code:', e);
                          console.log('QR Code data:', qrCode?.substring(0, 50) + '...');
                        }}
                      />
                    </div>
                    <div className="qr-instructions">
                      <h4>Como conectar:</h4>
                      <ol>
                        <li>Abra o WhatsApp no seu celular</li>
                        <li>Toque em "Mais opções" (⋮) ou "Configurações"</li>
                        <li>Selecione "Aparelhos conectados" ou "WhatsApp Web"</li>
                        <li>Toque em "Conectar um aparelho"</li>
                        <li>Escaneie o código QR acima</li>
                      </ol>
                      <div className="qr-info">
                        {qrRetryCount >= MAX_QR_RETRIES ? (
                          <div className="qr-exhausted">
                            <p><strong>⚠️ Tentativas esgotadas</strong></p>
                            <p>Após 10 renovações automáticas, o QR Code não foi escaneado.</p>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => selectedConnection && showQrCode(selectedConnection)}
                            >
                              <i className="fas fa-refresh"></i>
                              Novo QR Code
                            </button>
                          </div>
                        ) : (
                          <div className="qr-status">
                            <p><strong>📱 QR Code renovado automaticamente</strong></p>
                            <p>Atualização cada 60 segundos • {qrRetryCount + 1}/10 tentativas</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="qr-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>QR Code não disponível. A conexão pode ter expirado.</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => selectedConnection && showQrCode(selectedConnection)}
                    >
                      Tentar Novamente
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalPortal>
      </div>
      )}
    </div>
  );
};

export default WhatsApp;
